/**
 * Crescent Flexipack — Industry Detail Page.
 * Renders seafood-frozen-food (and any future industry) from the backend CMS.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Loader2, AlertTriangle, Thermometer, Shield, Palette, Globe } from "lucide-react";
import CrescentLayout from "./CrescentLayout";
import { useCrescentT, TTSPlayer } from "./CrescentI18n";
import usePageSeo, { useBreadcrumbSchema, useFaqSchema } from "@/hooks/usePageSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = [Thermometer, Shield, Palette, Globe];

export default function CrescentIndustryDetail() {
  const { slug } = useParams();
  const [raw, setRaw] = useState(null);
  const [state, setState] = useState("loading");
  const { t: industry } = useCrescentT(raw);

  useEffect(() => {
    axios.get(`${API}/crescent/industries/${slug}`)
      .then((r) => { setRaw(r.data.industry); setState("ok"); })
      .catch(() => setState("not-found"));
  }, [slug]);

  usePageSeo(industry
    ? { title: `${industry.title} — Crescent Flexi Pack`, description: (industry.tagline || "").slice(0, 170) }
    : { title: "Loading…", noindex: true }
  );
  useBreadcrumbSchema(industry ? [
    { name: "Home", url: "/crescent" },
    { name: "Industries", url: "/crescent/industries" },
    { name: industry.title, url: `/crescent/industries/${slug}` },
  ] : []);
  useFaqSchema(industry?.faqs?.map((f) => ({ question: f.q, answer: f.a })) || []);

  if (state === "loading") {
    return <CrescentLayout><div className="py-24 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" size={24} /></div></CrescentLayout>;
  }
  if (state === "not-found" || !industry) {
    return (
      <CrescentLayout>
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <AlertTriangle size={32} className="mx-auto text-amber-500" />
          <h1 className="mt-3 font-heading text-3xl font-bold text-slate-900">Industry not found</h1>
          <Link to="/crescent/industries" className="mt-5 inline-flex items-center gap-2 text-emerald-700 font-semibold">
            View all industries <ArrowRight size={14} />
          </Link>
        </div>
      </CrescentLayout>
    );
  }

  return (
    <CrescentLayout>
      <div data-testid={`crescent-industry-${slug}`}>
        <section className={`relative bg-cover bg-center text-white py-16 bg-gradient-to-br ${industry.fallback_gradient || 'from-emerald-500 via-emerald-600 to-teal-700'}`}
          style={industry.image ? { backgroundImage: `linear-gradient(135deg, rgba(16,185,129,0.72), rgba(13,148,136,0.78)), url(${industry.image})` } : {}}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="text-xs text-amber-100 mb-3">
              <Link to="/crescent" className="hover:underline">Home</Link>
              <span className="mx-2">›</span>
              <Link to="/crescent/industries" className="hover:underline">Industries</Link>
              <span className="mx-2">›</span>
              <span className="text-white">{industry.title}</span>
            </nav>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl leading-tight" data-testid="industry-title">{industry.title}</h1>
            <p className="mt-3 text-lg text-white/90 max-w-3xl">{industry.tagline}</p>
            <div className="mt-5">
              <TTSPlayer
                text={[industry.title, industry.tagline, ...(industry.intro || [])].filter(Boolean).join(". ")}
                label="Listen"
              />
            </div>
          </div>
        </section>

        <section className="py-14">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {(industry.intro || []).map((p, i) => (
              <p key={i} className="text-base text-slate-700 leading-relaxed mb-4">{p}</p>
            ))}
          </div>
        </section>

        {industry.considerations?.length > 0 && (
          <section className="pb-14">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="font-heading font-extrabold text-slate-900 text-3xl mb-6">Packaging considerations</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="industry-considerations">
                {industry.considerations.map((c, i) => {
                  const Icon = ICONS[i % ICONS.length];
                  return (
                    <div key={i} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`consideration-${i}`}>
                      <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
                        <Icon size={18} />
                      </div>
                      <h3 className="font-bold text-slate-900 mt-3">{c.heading}</h3>
                      <p className="text-sm text-slate-600 mt-1 leading-relaxed">{c.detail}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {industry.formats_recommended?.length > 0 && (
          <section className="pb-14 bg-slate-50">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
              <h2 className="font-heading font-extrabold text-slate-900 text-2xl mb-4">Recommended formats</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {industry.formats_recommended.map((f, i) => (
                  <div key={i} className="bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold shrink-0">{i + 1}</div>
                    <span className="text-sm text-slate-700">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {industry.faqs?.length > 0 && (
          <section className="pb-14">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="font-heading font-extrabold text-slate-900 text-3xl mb-6">Frequently Asked</h2>
              <div className="divide-y divide-slate-200 border border-slate-200 rounded-xl overflow-hidden" data-testid="industry-faqs">
                {industry.faqs.map((f, i) => (
                  <details key={i} className="group">
                    <summary className="flex items-start justify-between gap-4 px-5 py-4 cursor-pointer list-none hover:bg-slate-50">
                      <span className="font-semibold text-slate-900 text-sm">{f.q}</span>
                      <span className="text-emerald-600 text-xl font-bold group-open:rotate-45 transition-transform leading-none">+</span>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{f.a}</div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-14 bg-gradient-to-br from-emerald-600 to-emerald-800 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-extrabold text-3xl">Ready to specify your {industry.title.replace(/ Packaging$/, "")} packaging?</h2>
            <p className="mt-3 text-emerald-100">Our team will confirm the right structure, barrier and format for your application in writing.</p>
            <Link to="/crescent/request-quote" className="mt-6 inline-flex items-center gap-2 bg-white text-emerald-800 font-bold px-6 py-3 rounded-full">
              Request a Quote <ArrowRight size={16} />
            </Link>
          </div>
        </section>
      </div>
    </CrescentLayout>
  );
}
